import React, { useState, useEffect, useRef } from 'react';
import { Box, Container, Flex, Image, Text, Heading, Button, Stack, Grid, GridItem, Select, useToast, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton, useDisclosure, Tabs, TabList, TabPanels, Tab, TabPanel, Alert, AlertIcon, VStack, HStack, Icon, IconButton, Textarea, FormControl, FormLabel, FormErrorMessage, Input, Badge } from '@chakra-ui/react';
import ProductCard from '../components/ProductCard';
import FAQSection from '../components/FAQSection';
import Loader from '../components/Loader';
import SEO from '../components/SEO';
import ValentinesBanner from '../components/ValentinesBanner';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setCart } from '../features/cart/cartSlice';
import { motion, AnimatePresence } from 'framer-motion';
import { getProductDetails } from '../api/products';
import useDeviceType from '../hooks/useDeviceType';
import axios from 'axios';
import Cookies from 'js-cookie';
import { FaShieldAlt, FaTint, FaClock, FaPlus, FaMinus, FaStar, FaStarHalfAlt, FaCheckCircle, FaEdit, FaTrash } from 'react-icons/fa';
import { GiWaterSplash } from "react-icons/gi";
import { AiOutlineStar } from 'react-icons/ai';
import { getStrikePrice } from '../data/productPricing';

const MotionBox = motion(Box);
const MotionButton = motion(Button);
const MotionFlex = motion(Flex);

const ProductDescriptionPage = () => {
  const { productSlug } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const dispatch = useDispatch();
  const { isOpen, onOpen, onClose } = useDisclosure(); // For size chart modal
  const deviceType = useDeviceType();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mainImage, setMainImage] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedSizeStock, setSelectedSizeStock] = useState(null);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [zoomPosition, setZoomPosition] = useState({ x: 0, y: 0 });
  const [isZooming, setIsZooming] = useState(false);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [modalScale, setModalScale] = useState(1);
  const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 });
  const imageContainerRef = useRef(null);
  const [swipeDirection, setSwipeDirection] = useState(0); // 1 for right, -1 for left, 0 for none

  // Review states
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewsError, setReviewsError] = useState(null);
  const [reviewStats, setReviewStats] = useState({ average_rating: 0, total_reviews: 0 });
  const [currentReviewPage, setCurrentReviewPage] = useState(1);
  const [totalReviewPages, setTotalReviewPages] = useState(1);
  const [selectedReviewImage, setSelectedReviewImage] = useState(null);
  const { isOpen: isReviewImageOpen, onOpen: onReviewImageOpen, onClose: onReviewImageClose } = useDisclosure();

  // Review form states
  const [userReview, setUserReview] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const { isOpen: isReviewFormOpen, onOpen: onReviewFormOpen, onClose: onReviewFormClose } = useDisclosure();
  const [isEditingReview, setIsEditingReview] = useState(false);
  const [reviewFormData, setReviewFormData] = useState({
    rating: 5,
    review_text: '',
    review_image: null
  });
  const [reviewImagePreview, setReviewImagePreview] = useState(null);
  const [isSubmittingReview, setIsSubmittingReview] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const fileInputRef = useRef(null);

  // Sort images by order for gallery display
  const getSortedImages = () => {
    if (!product?.images || product.images.length === 0) return [];
    return [...product.images].sort((a, b) => a.order - b.order);
  };

  // Mobile swipe handlers
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    const sortedImages = getSortedImages();
    
    if (isLeftSwipe && currentImageIndex < sortedImages.length - 1) {
      setSwipeDirection(-1); // Swipe left
      const nextIndex = currentImageIndex + 1;
      setCurrentImageIndex(nextIndex);
      setMainImage(sortedImages[nextIndex].image);
    }
    
    if (isRightSwipe && currentImageIndex > 0) {
      setSwipeDirection(1); // Swipe right
      const prevIndex = currentImageIndex - 1;
      setCurrentImageIndex(prevIndex);
      setMainImage(sortedImages[prevIndex].image);
    }
    
    // Reset swipe direction after animation
    setTimeout(() => setSwipeDirection(0), 300);
  };

  // Pinch zoom handlers for modal
  const [touches, setTouches] = useState([]);
  const [lastDistance, setLastDistance] = useState(0);
  const [lastTap, setLastTap] = useState(0);

  const getDistance = (touch1, touch2) => {
    const dx = touch1.clientX - touch2.clientX;
    const dy = touch1.clientY - touch2.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const handleDoubleTap = () => {
    const currentTime = new Date().getTime();
    const tapLength = currentTime - lastTap;
    
    if (tapLength < 300 && tapLength > 0) {
      // Double tap detected
      if (modalScale === 1) {
        setModalScale(2.5);
      } else {
        setModalScale(1);
        setModalPosition({ x: 0, y: 0 });
      }
    }
    
    setLastTap(currentTime);
  };

  const handleModalTouchStart = (e) => {
    if (e.touches.length === 2) {
      const distance = getDistance(e.touches[0], e.touches[1]);
      setLastDistance(distance);
      setTouches([...e.touches]);
    }
  };

  const handleModalTouchMove = (e) => {
    if (e.touches.length === 2 && lastDistance > 0) {
      e.preventDefault();
      const distance = getDistance(e.touches[0], e.touches[1]);
      const scale = distance / lastDistance;
      const newScale = Math.min(Math.max(1, modalScale * scale), 4);
      
      setModalScale(newScale);
      setLastDistance(distance);
      
      if (newScale === 1) {
        setModalPosition({ x: 0, y: 0 });
      }
    }
  };

  const handleModalTouchEnd = () => {
    setLastDistance(0);
    setTouches([]);
  };

  // Quantity handlers
  const incrementQuantity = () => {
    const maxStock = selectedSizeStock !== null ? selectedSizeStock : 10;
    if (quantity < maxStock) {
      setQuantity(quantity + 1);
    }
  };

  const decrementQuantity = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1);
    }
  };

  // Handle zoom on hover for desktop
  const handleMouseMove = (e) => {
    if (window.innerWidth <= 768) return; // Skip on mobile/tablet
    
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setZoomPosition({ x, y });
  };

  const handleMouseEnter = () => {
    if (window.innerWidth > 768) {
      setIsZooming(true);
    }
  };

  const handleMouseLeave = () => {
    setIsZooming(false);
  };

  const handleImageClick = () => {
    setIsImageModalOpen(true);
    setModalScale(1);
    setModalPosition({ x: 0, y: 0 });
  };
  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const data = await getProductDetails(productSlug, deviceType);
        setProduct(data);
        
        // Set main image to primary image or first image
        if (data.images && data.images.length > 0) {
          const sortedImages = [...data.images].sort((a, b) => a.order - b.order);
          const primaryImage = sortedImages.find(img => img.isPrimary) || sortedImages[0];
          const primaryIndex = sortedImages.findIndex(img => img.isPrimary) || 0;
          setMainImage(primaryImage.image);
          setCurrentImageIndex(primaryIndex);
        } else {
          setMainImage('/no_image_available.jpg');
          setCurrentImageIndex(0);
        }
      } catch (err) {
        setError('Failed to fetch product details. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
    // Note: Scroll to top is handled globally by ScrollToTop component in App.js
  }, [productSlug, deviceType]);

  // Fetch reviews
  const fetchReviews = async (page = 1) => {
    try {
      setReviewsLoading(true);
      setReviewsError(null);
      const response = await axios.get(`/api/auth/reviews/product/${productSlug}/?page=${page}&page_size=10`);
      
      setReviews(response.data.results.reviews || []);
      setReviewStats({
        average_rating: response.data.results.product?.averageRating || 0,
        total_reviews: response.data.results.product?.totalReviews || 0
      });
      
      // Calculate total pages
      const totalCount = response.data.count || 0;
      const pageSize = 10;
      setTotalReviewPages(Math.ceil(totalCount / pageSize));
      setCurrentReviewPage(page);
    } catch (err) {
      console.error('Error fetching reviews:', err);
      setReviewsError('Failed to load reviews. Please try again later.');
      setReviews([]);
    } finally {
      setReviewsLoading(false);
    }
  };

  // Fetch reviews when product changes
  useEffect(() => {
    if (productSlug) {
      fetchReviews(1);
    }
  }, [productSlug]);

  // Check authentication and user's existing review
  useEffect(() => {
    const checkAuth = () => {
      const token = Cookies.get('accessToken');
      setIsAuthenticated(!!token);
    };
    
    checkAuth();
  }, []);

  // Check if user has already reviewed this product
  const checkUserReview = async () => {
    const token = Cookies.get('accessToken');
    if (!token) return;

    try {
      const response = await axios.get(`/api/auth/reviews/check/${productSlug}/`);
      if (response.data.hasReview) {
        setUserReview(response.data.review);
      } else {
        setUserReview(null);
      }
    } catch (err) {
      console.error('Error checking user review:', err);
    }
  };

  useEffect(() => {
    if (productSlug && isAuthenticated) {
      checkUserReview();
    }
  }, [productSlug, isAuthenticated]);

  // Handle review page change
  const handleReviewPageChange = (page) => {
    fetchReviews(page);
  };

  // Handle opening review form
  const handleOpenReviewForm = (isEdit = false) => {
    if (!isAuthenticated) {
      toast({
        title: "Authentication Required",
        description: "Please log in to write a review.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      navigate('/auth');
      return;
    }

    setIsEditingReview(isEdit);
    
    if (isEdit && userReview) {
      setReviewFormData({
        rating: userReview.rating,
        review_text: userReview.reviewText,
        review_image: null
      });
      setReviewImagePreview(userReview.reviewImage || null);
    } else {
      setReviewFormData({
        rating: 5,
        review_text: '',
        review_image: null
      });
      setReviewImagePreview(null);
    }
    
    setFormErrors({});
    onReviewFormOpen();
  };

  // Handle review form input changes
  const handleReviewInputChange = (field, value) => {
    setReviewFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  // Handle review image selection
  const handleReviewImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (6MB)
    if (file.size > 6 * 1024 * 1024) {
      setFormErrors(prev => ({ ...prev, review_image: 'Image too large. Maximum size is 6MB.' }));
      return;
    }

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setFormErrors(prev => ({ ...prev, review_image: 'Invalid file type. Use JPG, PNG, or WebP.' }));
      return;
    }

    setReviewFormData(prev => ({ ...prev, review_image: file }));
    setReviewImagePreview(URL.createObjectURL(file));
    setFormErrors(prev => ({ ...prev, review_image: null }));
  };

  // Remove review image
  const handleRemoveReviewImage = () => {
    setReviewFormData(prev => ({ ...prev, review_image: null }));
    setReviewImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Validate review form
  const validateReviewForm = () => {
    const errors = {};
    
    if (!reviewFormData.rating || reviewFormData.rating < 1 || reviewFormData.rating > 5) {
      errors.rating = 'Please select a rating between 1 and 5.';
    }
    
    if (!reviewFormData.review_text || reviewFormData.review_text.trim().length < 10) {
      errors.review_text = 'Review must be at least 10 characters long.';
    }
    
    if (reviewFormData.review_text && reviewFormData.review_text.length > 1000) {
      errors.review_text = 'Review cannot exceed 1000 characters.';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Submit review (create or update)
  const handleSubmitReview = async () => {
    if (!validateReviewForm()) return;

    setIsSubmittingReview(true);
    
    try {
      const formData = new FormData();
      
      if (isEditingReview) {
        // Update existing review
        formData.append('rating', reviewFormData.rating);
        formData.append('review_text', reviewFormData.review_text);
        
        if (reviewFormData.review_image) {
          formData.append('review_image', reviewFormData.review_image);
        }

        await axios.patch(`/api/auth/reviews/${userReview.id}/update/`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        toast({
          title: "Review Updated",
          description: "Your review has been updated successfully!",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } else {
        // Create new review
        formData.append('product_slug', productSlug);
        formData.append('rating', reviewFormData.rating);
        formData.append('review_text', reviewFormData.review_text);
        
        if (reviewFormData.review_image) {
          formData.append('review_image', reviewFormData.review_image);
        }

        await axios.post('/api/auth/reviews/create/', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        toast({
          title: "Review Submitted",
          description: "Thank you for your review!",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }

      // Refresh reviews and user review
      fetchReviews(currentReviewPage);
      checkUserReview();
      onReviewFormClose();
      
    } catch (err) {
      console.error('Error submitting review:', err);
      
      // Handle specific errors
      if (err.response?.status === 429) {
        toast({
          title: "Rate Limit Exceeded",
          description: err.response.data.error || "You've reached the edit limit. Please try again later.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } else if (err.response?.data) {
        const errorData = err.response.data;
        if (typeof errorData === 'object') {
          // Display field-specific errors
          const firstError = Object.values(errorData)[0];
          toast({
            title: "Error",
            description: Array.isArray(firstError) ? firstError[0] : firstError,
            status: "error",
            duration: 4000,
            isClosable: true,
          });
        } else {
          toast({
            title: "Error",
            description: "Failed to submit review. Please try again.",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
        }
      } else {
        toast({
          title: "Error",
          description: "Failed to submit review. Please try again.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } finally {
      setIsSubmittingReview(false);
    }
  };

  // Delete review
  const handleDeleteReview = async () => {
    if (!userReview) return;

    const confirmDelete = window.confirm('Are you sure you want to delete your review? This action cannot be undone.');
    if (!confirmDelete) return;

    try {
      await axios.delete(`/api/auth/reviews/${userReview.id}/delete/`);
      
      toast({
        title: "Review Deleted",
        description: "Your review has been deleted successfully.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      // Refresh reviews and user review
      fetchReviews(currentReviewPage);
      setUserReview(null);
      
    } catch (err) {
      console.error('Error deleting review:', err);
      toast({
        title: "Error",
        description: "Failed to delete review. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Handle review image click
  const handleReviewImageClick = (imageUrl) => {
    setSelectedReviewImage(imageUrl);
    onReviewImageOpen();
  };

  const handleSizeChange = (e) => {
    const size = e.target.value;
    setSelectedSize(size);

    if (size && product.sizesAvailable) {
      const sizeIndex = product.sizes.indexOf(parseInt(size));
      const stock = product.stockDivision[sizeIndex];
      setSelectedSizeStock(stock);
      if (quantity > stock) {
        setQuantity(1);
      }
    } else {
      setSelectedSizeStock(null);
    }
  };

  const handleAddToCart = async () => {
    if (product.sizesAvailable && !selectedSize) {
      toast({
        title: "Please select a size.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsAddingToCart(true);
    try {
      const response = await axios.post('/api/cart/add/', {
        product_id: product.id,
        quantity: quantity,
        size: selectedSize || null,
      });
      dispatch(setCart(response.data));
      toast({
        title: "Item added to cart.",
        description: `${product.name} (x${quantity}) has been added to your cart.`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      const errorMessage = error.response?.data?.quantity?.[0] || 
                         error.response?.data?.detail || 
                         'Could not add item to cart.';
      toast({
        title: 'Error',
        description: errorMessage,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsAddingToCart(false);
    }
  };

  if (loading) {
    return (
      <Container maxW="container.xl" py={10}>
        <Loader minH="400px" />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxW="container.xl" py={10}>
        <Alert status="error">
          <AlertIcon />
          {error}
        </Alert>
      </Container>
    );
  }

  if (!product) {
    return (
      <Container maxW="container.xl" py={10}>
        <Text fontSize="xl" textAlign="center">Product not found.</Text>
      </Container>
    );
  }

  // Helper function to render star ratings
  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    
    for (let i = 0; i < fullStars; i++) {
      stars.push(<Icon as={FaStar} key={`full-${i}`} color="gold" />);
    }
    
    if (hasHalfStar && stars.length < 5) {
      stars.push(<Icon as={FaStarHalfAlt} key="half" color="gold" />);
    }
    
    while (stars.length < 5) {
      stars.push(<Icon as={AiOutlineStar} key={`empty-${stars.length}`} color="gray.300" />);
    }
    
    return stars;
  };

  return (
    <>
      {/* SEO Meta Tags for Product */}
      {product && (
        <SEO 
          title={product.name}
          description={product.short_description || product.description?.substring(0, 160) || `Shop ${product.name} - Premium handcrafted jewelry from AGNIR's exclusive collection.`}
          keywords={`${product.name}, ${product.category}, AGNIR ${product.category}, premium ${product.category}, handcrafted ${product.category}, jewelry, fashion accessories`}
          image={mainImage ? `${process.env.REACT_APP_API_BASE_URL || 'https://agnir.co'}${mainImage}` : 'https://agnir.co/hero-bg.webp'}
          url={`https://agnir.co/product/${productSlug}`}
          product={{
            name: product.name,
            description: product.short_description || product.description?.substring(0, 160),
            image: mainImage ? `${process.env.REACT_APP_API_BASE_URL || 'https://agnir.co'}${mainImage}` : undefined,
            images: product.images?.map(img => `${process.env.REACT_APP_API_BASE_URL || 'https://agnir.co'}${img.image}`),
            price: product.price,
            sku: product.sku || product.slug,
            id: product.id,
            inStock: product.stock > 0,
            rating: reviewStats.average_rating,
            reviewCount: reviewStats.total_reviews,
            category: product.category
          }}
          breadcrumbs={[
            { name: 'Home', url: 'https://agnir.co/' },
            { name: product.category, url: `https://agnir.co/products/${product.category?.toLowerCase()}` },
            { name: product.name, url: `https://agnir.co/product/${productSlug}` }
          ]}
        />
      )}
      <Box bgImage={`url(${process.env.REACT_APP_BG_URL})`}>
      <Container maxW="container.xl" py={10} sx={{ fontFamily: "'Zain', sans-serif" }}>
        {/* Enhanced styling config for tabs & descriptive content */}
        {/* Keeping styles inline here for locality; could be refactored to theme extension later */}
        {/** NOTE: Using only black/white/gray palette per project guidelines */}
        <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={10}>
          <GridItem>
            {/* Image Gallery Section */}
            <Flex direction={{ base: 'column', md: 'row' }} gap={4}>
              {/* Thumbnail Gallery */}
              <Flex
                direction={{ base: 'row', md: 'column' }}
                gap={2}
                overflowX={{ base: 'auto', md: 'visible' }}
                overflowY={{ base: 'visible', md: 'auto' }}
                maxH={{ base: 'auto', md: '500px' }}
                order={{ base: 2, md: 1 }}
                sx={{
                  '&::-webkit-scrollbar': { 
                    width: '6px',
                    height: '6px'
                  },
                  '&::-webkit-scrollbar-track': { 
                    bg: 'gray.100' 
                  },
                  '&::-webkit-scrollbar-thumb': { 
                    bg: 'gray.400',
                    borderRadius: '3px'
                  }
                }}
              >
                {getSortedImages().length > 0 ? (
                  getSortedImages().map((img, index) => (
            <MotionBox
              key={index}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
            >
              
                  <Image
                    src={img.image}
                        alt={img.altText || `${product.name} view ${index + 1}`}
                        boxSize={{ base: '80px', md: '100px' }}
                    objectFit="cover"
                    cursor="pointer"
                    borderRadius="md"
                        border={mainImage === img.image ? '3px solid' : '2px solid transparent'}
                        borderColor={mainImage === img.image ? 'black' : 'transparent'}
                        onClick={() => setMainImage(img.image)}
                        _hover={{ 
                          borderColor: 'gray.400',
                          boxShadow: 'md'
                        }}
                        transition="all 0.2s"
                        flexShrink={0}
                      />
                    </MotionBox>
                ))
              ) : (
                <Image
                  src="/no_image_available.jpg"
                  alt="No image available"
                    boxSize={{ base: '80px', md: '100px' }}
                  objectFit="cover"
                  borderRadius="md"
                  border="2px solid transparent"
                />
              )}
              </Flex>

              {/* Main Image Display */}
              <Box 
                flex={1} 
                position="relative" 
                order={{ base: 1, md: 2 }}
                overflow="hidden"
                borderRadius="lg"
                bg="gray.50"
                boxShadow="lg"
                border="1px solid"
                borderColor="gray.200"
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
              >
                <MotionBox
                  key={mainImage}
                  initial={{ 
                    opacity: 0, 
                    x: swipeDirection === -1 ? 100 : swipeDirection === 1 ? -100 : 0,
                    scale: 0.95
                  }}
                  animate={{ 
                    opacity: 1, 
                    x: 0,
                    scale: 1
                  }}
                  exit={{ 
                    opacity: 0,
                    x: swipeDirection === -1 ? -100 : swipeDirection === 1 ? 100 : 0,
                    scale: 0.95
                  }}
                  transition={{ 
                    duration: 0.35,
                    ease: [0.4, 0, 0.2, 1]
                  }}
                  position="relative"
                  cursor={{ base: 'pointer', md: isZooming ? 'zoom-in' : 'pointer' }}
                  onClick={handleImageClick}
                  onMouseMove={handleMouseMove}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                  _hover={{
                    transform: { base: 'none', md: 'scale(1.02)' }
                  }}
                >
                  <Image 
                    src={mainImage} 
                    alt={product.name} 
                    objectFit="contain" 
                    w="100%" 
                    h="auto" 
                    maxH={{ base: '400px', md: '500px' }}
                    transition="transform 0.3s ease"
                    p={4}
                    userSelect="none"
                    pointerEvents="none"
                  />
                  
                  {/* Zoom Overlay for Desktop */}
                  {isZooming && (
                    <Box
                      position="absolute"
                      top={0}
                      left={0}
                      right={0}
                      bottom={0}
                      bg="white"
                      backgroundImage={`url(${mainImage})`}
                      backgroundSize="250%"
                      backgroundPosition={`${zoomPosition.x}% ${zoomPosition.y}%`}
                      backgroundRepeat="no-repeat"
                      opacity={0.95}
                      pointerEvents="none"
                      borderRadius="lg"
                      display={{ base: 'none', md: 'block' }}
                      zIndex={10}
                    />
                  )}
                  
                  {/* Swipe indicator for mobile */}
                  <Box
                    position="absolute"
                    bottom={4}
                    left="50%"
                    transform="translateX(-50%)"
                    bg="blackAlpha.700"
                    color="white"
                    px={3}
                    py={1}
                    borderRadius="full"
                    fontSize="xs"
                    display={{ base: 'flex', md: 'none' }}
                    fontWeight="medium"
                    alignItems="center"
                    gap={2}
                    pointerEvents="none"
                  >
                    ← Swipe → | Tap to zoom
                  </Box>
                  
                  {/* Image counter for mobile */}
                  <Box
                    position="absolute"
                    top={4}
                    right={4}
                    bg="blackAlpha.700"
                    color="white"
                    px={2}
                    py={1}
                    borderRadius="md"
                    fontSize="xs"
                    display={{ base: 'block', md: 'none' }}
                    fontWeight="bold"
                    pointerEvents="none"
                  >
                    {currentImageIndex + 1} / {getSortedImages().length || 1}
                  </Box>
                  
                  {/* Zoom indicator for desktop */}
                  <Box
                    position="absolute"
                    bottom={4}
                    right={4}
                    bg="blackAlpha.700"
                    color="white"
                    px={3}
                    py={1}
                    borderRadius="full"
                    fontSize="sm"
                    display={{ base: 'none', md: 'block' }}
                    fontWeight="medium"
                    opacity={isZooming ? 1 : 0.7}
                    transition="opacity 0.3s"
                  >
                  Hover to zoom
                  </Box>
                </MotionBox>
              </Box>
            </Flex>
          </GridItem>

          <GridItem>
            {/* Valentine's Day Banner */}
            <ValentinesBanner variant="inline" />
            
            <Stack spacing={4}>
              <Heading 
                as="h1" 
                fontSize={{ base: '4xl', md: '5xl' }}
                fontFamily="'Cormorant', serif"
                fontWeight="700"
                letterSpacing="0.5px"
                lineHeight="1.2"
                color="black"
              >
                {product.name}
              </Heading>
              
              {/* Price Section with Strikethrough */}
              <Flex direction="column" gap={2}>
                {getStrikePrice(product.id) && (
                  <Flex align="center" gap={3}>
                    <Box position="relative" display="inline-block">
                      <Text 
                        fontSize={{ base: '2xl', md: '3xl' }}
                        fontWeight="500" 
                        color="gray.600"
                        fontFamily="'Cormorant', serif"
                        letterSpacing="0.5px"
                        position="relative"
                        _after={{
                          content: '""',
                          position: 'absolute',
                          left: '0',
                          top: '50%',
                          width: '100%',
                          height: '2.5px',
                          backgroundColor: '#E53E3E',
                          transform: 'translateY(-50%) rotate(-5deg)',
                        }}
                      >
                        Rs {getStrikePrice(product.id)}
                      </Text>
                    </Box>
                    <Badge 
                      bg="#E53E3E"
                      color="white"
                      fontSize={{ base: 'sm', md: 'md' }}
                      fontWeight="700"
                      px={{ base: 3, md: 4 }}
                      py={{ base: 1, md: 1.5 }}
                      borderRadius="md"
                      fontFamily="'Zain', sans-serif"
                    >
                      {Math.round((1 - product.price / getStrikePrice(product.id)) * 100)}% OFF
                    </Badge>
                  </Flex>
                )}
                <Text 
                  fontSize={{ base: '4xl', md: '5xl' }}
                  fontWeight="800" 
                  color="black"
                  fontFamily="'Cormorant', serif"
                  letterSpacing="0.5px"
                  lineHeight="1"
                >
                  Rs {product.price}
                </Text>
              </Flex>
              
              {product.sizesAvailable && (
                <>
                  <Flex align="center" flexWrap="wrap" gap={2}>
                    <Text mr={2} fontWeight="700" fontSize="md" fontFamily="'Zain', sans-serif">Size:</Text>
                    <Select 
                      placeholder="Select size" 
                      value={selectedSize} 
                      onChange={handleSizeChange} 
                      width={{ base: "full", sm: "150px" }}
                      bg="white"
                      border="2px solid"
                      borderColor="black"
                      color="black"
                      fontWeight="600"
                      fontFamily="'Zain', sans-serif"
                      _hover={{ borderColor: "gray.700" }}
                      _focus={{ borderColor: "black", boxShadow: "0 0 0 1px black" }}
                    >
                      {product.sizes.map(size => (
                        <option key={size} value={size}>{size}</option>
                      ))}
                    </Select>
                    <Button 
                      onClick={onOpen} 
                      variant="link" 
                      color="black"
                      fontWeight="600"
                      textDecoration="underline"
                      fontFamily="'Zain', sans-serif"
                      _hover={{ color: "gray.700" }}
                    >
                      Size Chart
                    </Button>
                  </Flex>
                  {selectedSize && selectedSizeStock !== null && (
                    <Text 
                      color={selectedSizeStock === 0 ? 'red.500' : 'gray.700'} 
                      fontWeight="bold"
                      fontFamily="'Zain', sans-serif"
                      fontSize="sm"
                    >
                      {selectedSizeStock === 0 ? 'Out of stock' : selectedSizeStock === 1 ? `Only 1 left in stock!` : `${selectedSizeStock} available`}
                    </Text>
                  )}
                </>
              )}

              {/* Quantity Selector */}
              <Box>
                <Text mb={2} fontWeight="700" fontSize="md" fontFamily="'Zain', sans-serif">Quantity:</Text>
                <HStack spacing={0} display={{ base: 'flex', sm: 'flex' }}>
                  <IconButton
                    icon={<FaMinus />}
                    onClick={decrementQuantity}
                    isDisabled={quantity <= 1}
                    aria-label="Decrease quantity"
                    size="md"
                    bg="white"
                    border="2px solid"
                    borderColor="black"
                    borderRightWidth="1px"
                    color="black"
                    borderRadius="md"
                    borderTopRightRadius={0}
                    borderBottomRightRadius={0}
                    _hover={{ bg: "gray.100" }}
                    _active={{ bg: "gray.200" }}
                    _disabled={{ opacity: 0.4, cursor: "not-allowed" }}
                  />
                  <Box
                    px={6}
                    py={1}
                    bg="white"
                    border="2.2px solid"
                    borderColor="black"
                    borderLeftWidth="0"
                    borderRightWidth="0"
                    fontWeight="bold"
                    fontSize="lg"
                    minW="60px"
                    textAlign="center"
                  >
                    {quantity}
                  </Box>
                  <IconButton
                    icon={<FaPlus />}
                    onClick={incrementQuantity}
                    isDisabled={quantity >= (selectedSizeStock !== null ? selectedSizeStock : 10)}
                    aria-label="Increase quantity"
                    size="md"
                    bg="white"
                    border="2px solid"
                    borderColor="black"
                    borderLeftWidth="1px"
                    color="black"
                    borderRadius="md"
                    borderTopLeftRadius={0}
                    borderBottomLeftRadius={0}
                    _hover={{ bg: "gray.100" }}
                    _active={{ bg: "gray.200" }}
                    _disabled={{ opacity: 0.4, cursor: "not-allowed" }}
                  />
                </HStack>
              </Box>

              <MotionButton
                size="lg"
                onClick={handleAddToCart}
                isLoading={isAddingToCart}
                isDisabled={product.stock === 0 || (selectedSize && selectedSizeStock === 0)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                bg="black"
                color="white"
                fontWeight="bold"
                border="2px solid"
                borderColor="black"
                fontFamily="'Zain', sans-serif"
                fontSize="md"
                letterSpacing="0.5px"
                _hover={{ 
                  bg: "gray.800",
                  transform: "translateY(-2px)",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.3)"
                }}
                _active={{ 
                  bg: "gray.900",
                  transform: "translateY(0)"
                }}
                _disabled={{
                  bg: "gray.400",
                  borderColor: "gray.400",
                  cursor: "not-allowed",
                  opacity: 0.6
                }}
                w={{ base: "full", sm: "auto" }}
              >
                {product.stock === 0 || (selectedSize && selectedSizeStock === 0) ? 'Out of Stock' : 'Add to Cart'}
              </MotionButton>

              {/* Product Features Section */}
              <Box 
                mt={6} 
                p={4} 
                bg="transparent" 
                
              >
                  
                  
                  <VStack spacing={3}>
                    <HStack spacing={3} align="center" w="full">
                      <Icon as={FaShieldAlt} color="black" boxSize={9} />
                      <VStack align="start" spacing={0} flex={1}>
                        <Text fontWeight="bold" fontSize="sm" color="gray.800" fontFamily="'Zain', sans-serif">
                          Anti-Tarnish Protection
                        </Text>
                        <Text fontSize="xs" color="gray.600" fontFamily="'Zain', sans-serif">
                          Stays brilliant & lustrous for years
                        </Text>
                      </VStack>
                    </HStack>

                    <HStack spacing={3} align="center" w="full">
                      {/* <Icon as={FaTint} color="blue.500" boxSize={5} /> */}
                      <GiWaterSplash size="2.5em"/>
                      <VStack align="start" spacing={0} flex={1}>
                        <Text fontWeight="bold" fontSize="sm" color="gray.800" fontFamily="'Zain', sans-serif">
                          100% Waterproof
                        </Text>
                        <Text fontSize="xs" color="gray.600" fontFamily="'Zain', sans-serif">
                          Swim, shower & sweat worry-free
                        </Text>
                      </VStack>
                    </HStack>

                    <HStack spacing={3} align="center" w="full">
                      <Icon as={FaClock} color="black" boxSize={9} />
                      <VStack align="start" spacing={0} flex={1}>
                        <Text fontWeight="bold" fontSize="sm" color="gray.800" fontFamily="'Zain', sans-serif">
                          Built for Everyday Wear
                        </Text>
                        <Text fontSize="xs" color="gray.600" fontFamily="'Zain', sans-serif">
                          Designed to withstand daily adventures
                        </Text>
                      </VStack>
                    </HStack>
                  </VStack>
                
              </Box>
            </Stack>
          </GridItem>
        </Grid>

        <Box mt={10} maxW={{ base: "100%", md: "85%", lg: "80%" }} mx="auto">
          <Tabs
            variant="unstyled"
          >
            <TabList
              bg="rgba(255,255,255,0.82)"
              backdropFilter="blur(6px)"
              border="1px solid"
              borderColor="gray.300"
              borderRadius="xl"
              p={{ base: 1, md: 2 }}
              boxShadow="0 4px 12px rgba(0,0,0,0.08)"
              position="relative"
              display="flex"
              gap={{ base: 1, md: 2 }}
              justifyContent="center"
              _before={{
                content: '""',
                position: 'absolute',
                inset: 0,
                borderRadius: 'inherit',
                boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.04)'
              }}
            >
              {['Product Details', `Reviews (${reviewStats.total_reviews})`, 'FAQ'].map(label => (
                <Tab
                  key={label}
                  fontWeight="600"
                  fontSize={{ base: 'sm', md: 'md' }}
                  letterSpacing="0.3px"
                  color="gray.700"
                  px={{ base: 3, md: 6 }}
                  py={{ base: 2, md: 3 }}
                  borderRadius="md"
                  position="relative"
                  transition="all 0.25s ease"
                  flex={{ base: 1, md: "1" }}
                  _hover={{ bg: 'gray.100', color: 'black' }}
                  _selected={{
                    bg: 'black',
                    color: 'white',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
                    transform: 'translateY(-2px)'
                  }}
                  _focusVisible={{
                    boxShadow: '0 0 0 3px rgba(0,0,0,0.4)',
                    outline: 'none'
                  }}
                  _active={{ bg: 'gray.800', color: 'white' }}
                  fontFamily="'Zain', sans-serif"
                  whiteSpace="nowrap"
                >
                  {label}
                </Tab>
              ))}
            </TabList>
            <TabPanels mt={4}>
              {/* Product Details Panel */}
              <TabPanel
                bg="rgba(255,255,255,0.9)"
                border="1px solid"
                borderColor="gray.300"
                borderRadius="xl"
                p={{ base: 4, md: 6 }}
                boxShadow="0 4px 14px rgba(0,0,0,0.08)"
              >
                <Heading
                  as="h3"
                  fontSize={{ base: '2xl', md: '3xl' }}
                  mb={4}
                  fontFamily="'Cormorant', serif"
                  color="black"
                  letterSpacing="0.5px"
                  fontWeight="700"
                >
                  Product Overview
                </Heading>
                <Stack spacing={4}>
                  {(product.detailedDescription || '').split(/\n+/).filter(Boolean).map((para, idx) => (
                    <Text
                      key={idx}
                      fontSize={{ base: 'lg', md: 'xl' }}
                      lineHeight={1.8}
                      color="black"
                      fontWeight="700"
                      letterSpacing="0.3px"
                      fontFamily="'Cormorant', serif"
                      textAlign="justify" 
                    >
                      {para}
                    </Text>
                  ))}
                  {!product.detailedDescription && (
                    <Text fontSize={{ base: 'lg', md: 'xl' }} color="gray.600" fontFamily="'Cormorant', serif" fontWeight="500">No additional description provided.</Text>
                  )}
                </Stack>
              </TabPanel>

              {/* Reviews Panel */}
              <TabPanel
                bg="rgba(255,255,255,0.9)"
                border="1px solid"
                borderColor="gray.300"
                borderRadius="xl"
                p={{ base: 4, md: 6 }}
                boxShadow="0 4px 14px rgba(0,0,0,0.08)"
                maxH={{ base: '600px', md: '800px' }}
                overflowY="auto"
                sx={{
                  '&::-webkit-scrollbar': {
                    width: '8px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: 'gray.100',
                    borderRadius: 'full',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: 'gray.400',
                    borderRadius: 'full',
                    _hover: {
                      background: 'gray.500',
                    },
                  },
                }}
              >
                <Flex justify="space-between" align="center" mb={6} flexWrap="wrap" gap={3}>
                  <Heading
                    as="h3"
                    fontSize={{ base: '2xl', md: '3xl' }}
                    fontFamily="'Cormorant', serif"
                    color="black"
                    letterSpacing="0.5px"
                    fontWeight="700"
                  >
                    Customer Reviews
                  </Heading>

                  {/* Review Action Buttons */}
                  {isAuthenticated && (
                    <HStack spacing={2}>
                      {userReview ? (
                        <>
                          <Button
                            size="sm"
                            bg="white"
                            color="black"
                            border="1px solid"
                            borderColor="gray.300"
                            fontFamily="'Zain', sans-serif"
                            fontWeight="600"
                            leftIcon={<Icon as={FaEdit} />}
                            onClick={() => handleOpenReviewForm(true)}
                            isDisabled={!userReview.canEdit}
                            _hover={{ bg: 'gray.50' }}
                            _disabled={{ opacity: 0.5, cursor: 'not-allowed' }}
                          >
                            Edit Review
                          </Button>
                          <IconButton
                            size="sm"
                            icon={<Icon as={FaTrash} />}
                            bg="white"
                            color="red.600"
                            border="1px solid"
                            borderColor="red.300"
                            aria-label="Delete review"
                            onClick={handleDeleteReview}
                            _hover={{ bg: 'red.50' }}
                          />
                        </>
                      ) : (
                        <Button
                          size="sm"
                          bg="black"
                          color="white"
                          fontFamily="'Zain', sans-serif"
                          fontWeight="600"
                          onClick={() => handleOpenReviewForm(false)}
                          _hover={{ bg: 'gray.800' }}
                          _active={{ bg: 'gray.900' }}
                        >
                          Write a Review
                        </Button>
                      )}
                    </HStack>
                  )}
                </Flex>

                {/* Rate Limit Warning */}
                {userReview && !userReview.canEdit && (
                  <Alert status="warning" mb={4} borderRadius="md">
                    <AlertIcon />
                    <Text fontSize="sm" fontFamily="'Zain', sans-serif">
                      You've reached the edit limit (3 edits per hour). Please try again later.
                    </Text>
                  </Alert>
                )}

                {/* Reviews Summary */}
                {reviewStats.total_reviews > 0 && (
                  <Box
                    mb={6}
                    p={{ base: 4, md: 5 }}
                    bg="white"
                    border="2px solid"
                    borderColor="gray.200"
                    borderRadius="lg"
                    boxShadow="0 2px 8px rgba(0,0,0,0.05)"
                  >
                    <Flex
                      direction={{ base: 'column', sm: 'row' }}
                      align={{ base: 'flex-start', sm: 'center' }}
                      justify="space-between"
                      gap={3}
                    >
                      <VStack align={{ base: 'flex-start', sm: 'center' }} spacing={1}>
                        <HStack spacing={1}>
                          {renderStars(reviewStats.average_rating)}
                        </HStack>
                        <Text
                          fontSize={{ base: '2xl', md: '3xl' }}
                          fontWeight="700"
                          fontFamily="'Cormorant', serif"
                          color="black"
                        >
                          {reviewStats.average_rating.toFixed(1)} out of 5
                        </Text>
                      </VStack>
                      <Text
                        fontSize={{ base: 'md', md: 'lg' }}
                        color="gray.600"
                        fontFamily="'Zain', sans-serif"
                        fontWeight="500"
                      >
                        Based on {reviewStats.total_reviews} {reviewStats.total_reviews === 1 ? 'review' : 'reviews'}
                      </Text>
                    </Flex>
                  </Box>
                )}

                {/* Loading State */}
                {reviewsLoading && (
                  <Loader minH="300px" />
                )}

                {/* Error State */}
                {reviewsError && !reviewsLoading && (
                  <Alert status="error" borderRadius="lg">
                    <AlertIcon />
                    {reviewsError}
                  </Alert>
                )}

                {/* Reviews List */}
                {!reviewsLoading && !reviewsError && (
                  <>
                    {reviews.length > 0 ? (
                      <Stack spacing={5}>
                        {reviews.map(review => (
                          <Box
                            key={review.id}
                            p={{ base: 4, md: 5 }}
                            border="1px solid"
                            borderColor="gray.300"
                            borderRadius="lg"
                            bg="white"
                            _hover={{ boxShadow: '0 4px 10px rgba(0,0,0,0.08)' }}
                            transition="box-shadow 0.25s ease"
                          >
                            {/* Review Header */}
                            <Flex
                              justify="space-between"
                              align="flex-start"
                              direction={{ base: 'column', sm: 'row' }}
                              gap={3}
                              mb={3}
                            >
                              <VStack align="flex-start" spacing={2} flex={1}>
                                <HStack spacing={3} flexWrap="wrap">
                                  <Text
                                    fontWeight="700"
                                    fontSize={{ base: 'lg', md: 'xl' }}
                                    color="black"
                                    fontFamily="'Cormorant', serif"
                                  >
                                    {review.userDisplayName}
                                  </Text>
                                  {review.isVerifiedPurchase && (
                                    <HStack
                                      spacing={1}
                                      px={2}
                                      py={1}
                                      bg="green.50"
                                      borderRadius="md"
                                      border="1px solid"
                                      borderColor="green.200"
                                    >
                                      <Icon as={FaCheckCircle} color="green.500" boxSize={3} />
                                      <Text
                                        fontSize="xs"
                                        color="green.700"
                                        fontWeight="600"
                                        fontFamily="'Zain', sans-serif"
                                      >
                                        Verified Purchase
                                      </Text>
                                    </HStack>
                                  )}
                                  {!review.isVerifiedPurchase && (
                                    <HStack
                                      spacing={1}
                                      px={2}
                                      py={1}
                                      bg="gray.50"
                                      borderRadius="md"
                                      border="1px solid"
                                      borderColor="gray.200"
                                    >
                                      <Text
                                        fontSize="xs"
                                        color="gray.600"
                                        fontWeight="600"
                                        fontFamily="'Zain', sans-serif"
                                      >
                                        Unverified
                                      </Text>
                                    </HStack>
                                  )}
                                </HStack>
                                <HStack spacing={1}>
                                  {renderStars(review.rating)}
                                </HStack>
                              </VStack>
                              <Text
                                fontSize="sm"
                                color="gray.500"
                                fontFamily="'Zain', sans-serif"
                                whiteSpace="nowrap"
                              >
                                {new Date(review.createdAt).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric'
                                })}
                              </Text>
                            </Flex>

                            {/* Review Body */}
                            <Flex
                              direction={{ base: 'column', md: review.reviewImage ? 'row' : 'column' }}
                              gap={4}
                            >
                              {review.reviewImage && (
                                <Box
                                  flexShrink={0}
                                  w={{ base: '100%', md: '120px' }}
                                  h={{ base: '150px', md: '120px' }}
                                  overflow="hidden"
                                  borderRadius="md"
                                  border="1px solid"
                                  borderColor="gray.200"
                                  cursor="pointer"
                                  onClick={() => handleReviewImageClick(review.reviewImage)}
                                  _hover={{ opacity: 0.8 }}
                                  transition="opacity 0.2s ease"
                                >
                                  <Image
                                    src={review.reviewImage}
                                    alt="Review"
                                    w="100%"
                                    h="100%"
                                    objectFit="cover"
                                  />
                                </Box>
                              )}
                              <Box flex={1}>
                                <Text
                                  fontSize={{ base: 'md', md: 'lg' }}
                                  lineHeight={1.75}
                                  color="gray.700"
                                  fontFamily="'Zain', sans-serif"
                                  fontWeight="500"
                                >
                                  {review.reviewText}
                                </Text>
                                {review.editCount > 0 && (
                                  <Text
                                    mt={2}
                                    fontSize="xs"
                                    color="gray.500"
                                    fontFamily="'Zain', sans-serif"
                                    fontStyle="italic"
                                  >
                                    Edited {review.editCount} {review.editCount === 1 ? 'time' : 'times'}
                                  </Text>
                                )}
                              </Box>
                            </Flex>
                          </Box>
                        ))}
                      </Stack>
                    ) : (
                      <VStack spacing={4} py={8}>
                        <Text
                          color="gray.600"
                          fontFamily="'Zain', sans-serif"
                          fontSize={{ base: 'md', md: 'lg' }}
                          textAlign="center"
                        >
                          No reviews yet.
                        </Text>
                        <Button
                          bg="black"
                          color="white"
                          fontFamily="'Zain', sans-serif"
                          fontWeight="600"
                          size="md"
                          _hover={{ bg: 'gray.800' }}
                          _active={{ bg: 'gray.900' }}
                          onClick={() => handleOpenReviewForm(false)}
                        >
                          Be the first to review
                        </Button>
                      </VStack>
                    )}

                    {/* Pagination */}
                    {reviews.length > 0 && totalReviewPages > 1 && (
                      <Flex
                        mt={6}
                        justify="center"
                        align="center"
                        gap={2}
                        flexWrap="wrap"
                      >
                        <Button
                          size="sm"
                          onClick={() => handleReviewPageChange(currentReviewPage - 1)}
                          isDisabled={currentReviewPage === 1}
                          bg="white"
                          border="1px solid"
                          borderColor="gray.300"
                          color="black"
                          fontFamily="'Zain', sans-serif"
                          _hover={{ bg: 'gray.50' }}
                          _disabled={{ opacity: 0.4, cursor: 'not-allowed' }}
                        >
                          Previous
                        </Button>
                        
                        {[...Array(totalReviewPages)].map((_, index) => {
                          const pageNum = index + 1;
                          // Show first page, last page, current page, and pages around current
                          const showPage = 
                            pageNum === 1 ||
                            pageNum === totalReviewPages ||
                            (pageNum >= currentReviewPage - 1 && pageNum <= currentReviewPage + 1);
                          
                          const showEllipsis = 
                            (pageNum === 2 && currentReviewPage > 3) ||
                            (pageNum === totalReviewPages - 1 && currentReviewPage < totalReviewPages - 2);

                          if (showEllipsis) {
                            return <Text key={pageNum} color="gray.500">...</Text>;
                          }

                          if (!showPage) return null;

                          return (
                            <Button
                              key={pageNum}
                              size="sm"
                              onClick={() => handleReviewPageChange(pageNum)}
                              bg={currentReviewPage === pageNum ? 'black' : 'white'}
                              color={currentReviewPage === pageNum ? 'white' : 'black'}
                              border="1px solid"
                              borderColor={currentReviewPage === pageNum ? 'black' : 'gray.300'}
                              fontFamily="'Zain', sans-serif"
                              fontWeight={currentReviewPage === pageNum ? '600' : '500'}
                              _hover={{
                                bg: currentReviewPage === pageNum ? 'gray.800' : 'gray.50'
                              }}
                            >
                              {pageNum}
                            </Button>
                          );
                        })}

                        <Button
                          size="sm"
                          onClick={() => handleReviewPageChange(currentReviewPage + 1)}
                          isDisabled={currentReviewPage === totalReviewPages}
                          bg="white"
                          border="1px solid"
                          borderColor="gray.300"
                          color="black"
                          fontFamily="'Zain', sans-serif"
                          _hover={{ bg: 'gray.50' }}
                          _disabled={{ opacity: 0.4, cursor: 'not-allowed' }}
                        >
                          Next
                        </Button>
                      </Flex>
                    )}
                  </>
                )}
              </TabPanel>

              {/* FAQ Panel */}
              <TabPanel
                bg="rgba(255,255,255,0.9)"
                border="1px solid"
                borderColor="gray.300"
                borderRadius="xl"
                p={{ base: 4, md: 6 }}
                boxShadow="0 4px 14px rgba(0,0,0,0.08)"
              >
                <Heading
                  as="h3"
                  fontSize={{ base: 'xl', md: '2xl' }}
                  mb={4}
                  fontFamily="'Zain', sans-serif"
                  color="black"
                  letterSpacing="0.5px"
                >
                  Frequently Asked Questions
                </Heading>
                <FAQSection
                  showTitle={false}
                  maxWidth="100%"
                  spacing={4}
                  variant="compact"
                />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Box>

        <Box mt={10}>
          <Heading as="h2" size="lg" mb={4} fontFamily="'Zain', sans-serif">Recommended Products</Heading>
          {product.recommendedProducts && product.recommendedProducts.length > 0 ? (
            <>
              {/* Desktop View */}
              <Grid 
                templateColumns={{ 
                  md: 'repeat(auto-fill, minmax(250px, 1fr))',
                  lg: 'repeat(auto-fill, minmax(280px, 1fr))'
                }} 
                gap={6} 
                display={{ base: 'none', md: 'grid' }}
                justifyItems="center"
              >
                {product.recommendedProducts.map(recProduct => (
                  <GridItem key={recProduct.id} maxW="320px" w="100%">
                    <ProductCard product={recProduct} />
                  </GridItem>
                ))}
              </Grid>

              {/* Mobile View */}
              <MotionFlex
                display={{ base: 'flex', md: 'none' }}
                overflowX="auto"
                whiteSpace="nowrap"
                py={2} // Add some vertical padding for scrollbar
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6 }}
                sx={{
                  '&::-webkit-scrollbar': { display: 'none' },
                  '-ms-overflow-style': 'none',
                  'scrollbar-width': 'none',
                }} // Hide scrollbar
              >
                {product.recommendedProducts.map(recProduct => (
                  <Box key={recProduct.id} minW={{ base: '280px', sm: '300px' }} mr={4} flexShrink={0} p={2}> {/* Adjust minW and mr for card sizing and spacing */}
                    <ProductCard product={recProduct} />
                  </Box>
                ))}
              </MotionFlex>
            </>
          ) : (
            <Text>No recommended products available.</Text>
          )}
        </Box>

{/* Image Enlargement Modal with Pinch Zoom */}
        <Modal 
          isOpen={isImageModalOpen} 
          onClose={() => {
            setIsImageModalOpen(false);
            setModalScale(1);
            setModalPosition({ x: 0, y: 0 });
          }} 
          size="full"
          isCentered
          closeOnOverlayClick={true}
        >
          <ModalOverlay 
            bg="blackAlpha.900" 
            onClick={() => {
              setIsImageModalOpen(false);
              setModalScale(1);
              setModalPosition({ x: 0, y: 0 });
            }}
          />
          <ModalContent bg="transparent" boxShadow="none" m={0}>
            <ModalCloseButton 
              color="white" 
              size="lg" 
              bg="blackAlpha.700" 
              _hover={{ bg: "blackAlpha.800" }}
              borderRadius="full"
              top={4}
              right={4}
              zIndex={2}
            />
            <ModalBody 
              p={0} 
              display="flex" 
              alignItems="center" 
              justifyContent="center"
              overflow="hidden"
              position="relative"
              h="100vh"
              w="100vw"
              onTouchStart={handleModalTouchStart}
              onTouchMove={handleModalTouchMove}
              onTouchEnd={handleModalTouchEnd}
            >
              <MotionBox
                drag={modalScale > 1}
                dragConstraints={{ left: 0, right: 0, top: 0, bottom: 0 }}
                dragElastic={0.1}
                style={{
                  scale: modalScale,
                  x: modalPosition.x,
                  y: modalPosition.y,
                  cursor: modalScale > 1 ? 'grab' : 'default'
                }}
                whileTap={{ cursor: modalScale > 1 ? 'grabbing' : 'default' }}
                onWheel={(e) => {
                  e.preventDefault();
                  const delta = e.deltaY * -0.01;
                  const newScale = Math.min(Math.max(1, modalScale + delta), 4);
                  setModalScale(newScale);
                  if (newScale === 1) {
                    setModalPosition({ x: 0, y: 0 });
                  }
                }}
                animate={{ scale: modalScale }}
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleDoubleTap();
                }}
              >
                <Image 
                  src={mainImage} 
                  alt={product.name}
                  maxW="90vw"
                  maxH="90vh"
                  objectFit="contain"
                  userSelect="none"
                  pointerEvents="none"
                  style={{
                    touchAction: 'none'
                  }}
                />
              </MotionBox>
              
              {/* Zoom instructions */}
              <Box
                position="absolute"
                bottom={4}
                left="50%"
                transform="translateX(-50%)"
                bg="blackAlpha.700"
                color="white"
                px={4}
                py={2}
                borderRadius="full"
                fontSize="sm"
                fontWeight="medium"
                pointerEvents="none"
              >
                {modalScale === 1 ? 'Double tap to zoom | Pinch to zoom' : 'Drag to pan | Double tap to reset'}
              </Box>
            </ModalBody>
          </ModalContent>
        </Modal>

        {/* Review Image Modal */}
        <Modal isOpen={isReviewImageOpen} onClose={onReviewImageClose} size="xl" isCentered>
          <ModalOverlay bg="blackAlpha.800" />
          <ModalContent bg="transparent" boxShadow="none" maxW={{ base: '90vw', md: '80vw', lg: '70vw' }}>
            <ModalCloseButton
              color="white"
              bg="blackAlpha.600"
              _hover={{ bg: 'blackAlpha.800' }}
              size="lg"
              zIndex={2}
            />
            <ModalBody p={0}>
              <Image
                src={selectedReviewImage}
                alt="Review Image"
                w="100%"
                h="auto"
                maxH="90vh"
                objectFit="contain"
                borderRadius="md"
              />
            </ModalBody>
          </ModalContent>
        </Modal>

        {/* Review Form Modal */}
        <Modal isOpen={isReviewFormOpen} onClose={onReviewFormClose} size="xl" isCentered>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader fontFamily="'Cormorant', serif" fontSize="2xl" fontWeight="700">
              {isEditingReview ? 'Edit Your Review' : 'Write a Review'}
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={6}>
              <VStack spacing={5} align="stretch">
                {/* Star Rating Selector */}
                <FormControl isInvalid={formErrors.rating}>
                  <FormLabel fontFamily="'Zain', sans-serif" fontWeight="600" color="black">
                    Rating
                  </FormLabel>
                  <HStack spacing={2}>
                    {[1, 2, 3, 4, 5].map((star) => (
                      <IconButton
                        key={star}
                        icon={<Icon as={star <= reviewFormData.rating ? FaStar : AiOutlineStar} boxSize={6} />}
                        onClick={() => handleReviewInputChange('rating', star)}
                        color={star <= reviewFormData.rating ? 'gold' : 'gray.300'}
                        bg="transparent"
                        _hover={{ transform: 'scale(1.1)' }}
                        _active={{ transform: 'scale(0.95)' }}
                        transition="all 0.2s"
                        aria-label={`Rate ${star} stars`}
                      />
                    ))}
                    <Text ml={3} fontFamily="'Zain', sans-serif" color="gray.600" fontSize="sm">
                      {reviewFormData.rating} {reviewFormData.rating === 1 ? 'star' : 'stars'}
                    </Text>
                  </HStack>
                  <FormErrorMessage fontFamily="'Zain', sans-serif">{formErrors.rating}</FormErrorMessage>
                </FormControl>

                {/* Review Text */}
                <FormControl isInvalid={formErrors.review_text}>
                  <FormLabel fontFamily="'Zain', sans-serif" fontWeight="600" color="black">
                    Your Review
                  </FormLabel>
                  <Textarea
                    value={reviewFormData.review_text}
                    onChange={(e) => handleReviewInputChange('review_text', e.target.value)}
                    placeholder="Share your experience with this product..."
                    fontFamily="'Zain', sans-serif"
                    fontSize="md"
                    minH="150px"
                    resize="vertical"
                    _focus={{ borderColor: 'black', boxShadow: '0 0 0 1px black' }}
                  />
                  <Flex justify="space-between" mt={1}>
                    <FormErrorMessage fontFamily="'Zain', sans-serif">{formErrors.review_text}</FormErrorMessage>
                    <Text
                      fontSize="xs"
                      color={reviewFormData.review_text.length > 1000 ? 'red.500' : 'gray.500'}
                      fontFamily="'Zain', sans-serif"
                      ml="auto"
                    >
                      {reviewFormData.review_text.length} / 1000
                    </Text>
                  </Flex>
                </FormControl>

                {/* Image Upload */}
                <FormControl isInvalid={formErrors.review_image}>
                  <FormLabel fontFamily="'Zain', sans-serif" fontWeight="600" color="black">
                    Add Photo (Optional)
                  </FormLabel>
                  
                  {reviewImagePreview ? (
                    <Box position="relative" w="200px" h="200px" borderRadius="md" overflow="hidden" border="1px solid" borderColor="gray.300">
                      <Image src={reviewImagePreview} alt="Review preview" w="100%" h="100%" objectFit="cover" />
                      <IconButton
                        icon={<Icon as={FaTrash} />}
                        position="absolute"
                        top={2}
                        right={2}
                        size="sm"
                        colorScheme="red"
                        onClick={handleRemoveReviewImage}
                        aria-label="Remove image"
                      />
                    </Box>
                  ) : (
                    <Input
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/webp"
                      onChange={handleReviewImageSelect}
                      ref={fileInputRef}
                      display="none"
                    />
                  )}
                  
                  {!reviewImagePreview && (
                    <Button
                      onClick={() => fileInputRef.current?.click()}
                      bg="white"
                      border="1px solid"
                      borderColor="gray.300"
                      fontFamily="'Zain', sans-serif"
                      _hover={{ bg: 'gray.50' }}
                    >
                      Choose Image
                    </Button>
                  )}
                  
                  <Text fontSize="xs" color="gray.500" mt={2} fontFamily="'Zain', sans-serif">
                    Max 6MB • JPG, PNG, or WebP
                  </Text>
                  <FormErrorMessage fontFamily="'Zain', sans-serif">{formErrors.review_image}</FormErrorMessage>
                </FormControl>

                {/* Submit Button */}
                <HStack spacing={3} pt={4}>
                  <Button
                    onClick={onReviewFormClose}
                    variant="outline"
                    fontFamily="'Zain', sans-serif"
                    flex={1}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSubmitReview}
                    bg="black"
                    color="white"
                    fontFamily="'Zain', sans-serif"
                    fontWeight="600"
                    isLoading={isSubmittingReview}
                    loadingText={isEditingReview ? 'Updating...' : 'Submitting...'}
                    _hover={{ bg: 'gray.800' }}
                    _active={{ bg: 'gray.900' }}
                    flex={1}
                  >
                    {isEditingReview ? 'Update Review' : 'Submit Review'}
                  </Button>
                </HStack>
              </VStack>
            </ModalBody>
          </ModalContent>
        </Modal>

        <Modal isOpen={isOpen} onClose={onClose} size={{ base: "full", md: "2xl" }} scrollBehavior="inside">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader fontFamily="'Cormorant', serif" fontSize="2xl" fontWeight="700">Ring Size Guide</ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={6}>
              <VStack spacing={6} align="stretch">
                {/* Size Chart Table */}
                <Box>
                  <Heading 
                    as="h3" 
                    size="md" 
                    mb={3} 
                    fontFamily="'Cormorant', serif" 
                    fontWeight="700"
                    textAlign="center"
                  >
                    Size Chart
                  </Heading>
                  <Image 
                    src="/Size_Chart_Table.webp" 
                    alt="Ring Size Chart Table" 
                    objectFit="contain" 
                    w="100%" 
                    borderRadius="md"
                    boxShadow="sm"
                  />
                </Box>

                {/* How to Measure Section */}
                <Box>
                  <Heading 
                    as="h3" 
                    size="md" 
                    mb={3} 
                    fontFamily="'Cormorant', serif" 
                    fontWeight="700"
                    textAlign="center"
                  >
                    How to Measure Your Ring Size
                  </Heading>
                  <Image 
                    src="/Size_Chart.webp" 
                    alt="How to Measure Ring Size" 
                    objectFit="contain" 
                    w="100%" 
                    borderRadius="md"
                    boxShadow="sm"
                  />
                </Box>

                {/* Additional Info */}
                <Box 
                  bg="gray.50" 
                  p={4} 
                  borderRadius="md" 
                  borderLeft="4px solid" 
                  borderColor="black"
                >
                  <Text fontFamily="'Zain', sans-serif" fontSize="sm" color="gray.700" fontWeight="500">
                    <strong>Note:</strong> If you're between sizes, we suggest choosing the larger size for comfort.
                  </Text>
                </Box>
              </VStack>
            </ModalBody>
          </ModalContent>
        </Modal>
      </Container>
    </Box>
    </>
  );
};

export default ProductDescriptionPage;
